import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useMemo, useState } from 'react'

import { ProductCard } from '@src/components/product-card/product-card.component'
import {
  getInitialSelectedVariant,
  getSelectedOptionsForProductCard,
} from '@src/components/product-card/product-card.helpers'
import { CardVariant } from '@src/components/product-card/product-card.types'
import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import { Button, BUTTON_SIZE, BUTTON_TYPES } from '@src/quince-components/src'
import { SmartLoaderWrapper } from '@src/quince-components/src/atoms/loaderWrapper'
import CUSTOM_SECTION_CONTAINER_NAMES from '@src/quince-site-common/src/constants/customSectionContainerNames'
import CUSTOM_SECTION_CONTAINER_TYPES from '@src/quince-site-common/src/constants/customSectionContainerTypes'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import productBuyNowStateManager from '@src/quince-site-common/src/redux/globalStateManager/ProductBuyNowStateManager'
import { MAX_NUMBER_OF_COLOR_OPTIONS } from '@src/quince-site-common/src/utils/constants'
import getMaxWidthInPx from '@src/quince-site-common/src/utils/getMaxWidthInPx'
import { FINDING_METHOD_AGGREGATE, setFindingMethod } from '@src/telemetry/findingMethod'

import { WishlistItem } from '../../wishlist.types'

import styles from './wishlist-item-list.module.scss'

const WISHLIST_DISPLAY_NAME = 'Wishlist'
const WISHLIST_TRACKER = 'wishlist'
const TRACK_CATEGORY = 'product-list'

function WishlistItemList({
  items,
  isLoading = false,
  sectionConfig: { tracker },
  isSelfWishlist,
}: {
  items: WishlistItem[] | undefined
  isLoading?: boolean
  sectionConfig: { tracker?: string }
  isSelfWishlist?: boolean
}) {
  const deviceInfo = useDeviceInfo()
  const [selectedVariants, setSelectedVariants] = useState<Record<string, CardVariant>>({})

  const maxWidth = getMaxWidthInPx({
    maxWidthMobileInRem: styles.productCardWidthMobile ?? '',
    maxWidthDesktopInRem: styles.productCardWidth ?? '',
    deviceType: deviceInfo.deviceType,
  })

  const reversedWishlistItems = useMemo(() => {
    return items ? [...items].reverse() : []
  }, [items])

  const { t } = useTranslation('wishlist')
  const { t: tCommon } = useTranslation('common')

  return (
    <SmartLoaderWrapper isLoading={isLoading} hasData={reversedWishlistItems.length > 0} minHeight="100vh">
      <div className={styles.wishListContainer}>
        {reversedWishlistItems.length > 0 ? (
          <div className={styles.wishListWrapper}>
            {reversedWishlistItems.map((productCard, index) => {
              const selectedCardVariant = getInitialSelectedVariant(productCard.product)
              const selectedColor = selectedCardVariant?.displayConfig.value
              const uniqueKey = `${productCard.product.productId}_${selectedColor}`

              return (
                <div
                  key={uniqueKey}
                  className={styles.wishListItemWrapper}
                  onClick={() => {
                    setFindingMethod[FINDING_METHOD_AGGREGATE.WISHLIST]?.()
                  }}
                >
                  <ProductCard
                    productCard={productCard}
                    maxWidth={maxWidth}
                    className={classNames(styles.productCard)}
                    tracker={tracker && `${tracker}__wishlist__${index}`}
                    hideBuyNowIcon
                    showDeleteBtn={isSelfWishlist}
                    optionLimit={MAX_NUMBER_OF_COLOR_OPTIONS}
                    shouldShowMore
                    productClickTrackConfig={{
                      trackInternal: true,
                      trackGA: true,
                      // @i18n-ignore
                      category: TRACK_CATEGORY,
                      action: `${tracker ?? WISHLIST_TRACKER}__click_product_wishlist`,
                      value: productCard.product.relatedIds.spid.toString(),
                      context: {
                        contentContainerType: CUSTOM_SECTION_CONTAINER_TYPES.PRODUCT_CARD,
                        contentContainerName: CUSTOM_SECTION_CONTAINER_NAMES.SUBCOLLECTION_DISPLAY,
                        // @i18n-ignore
                        contentContainerDisplayName: WISHLIST_DISPLAY_NAME,
                        findingMethod: FINDING_METHOD_AGGREGATE.WISHLIST,
                      },
                    }}
                    hideDeleteForOtherColors
                    onVariantChange={variant => {
                      setSelectedVariants(prev => ({
                        ...prev,
                        [uniqueKey]: variant,
                      }))
                    }}
                  />
                  {deviceInfo.deviceType === DEVICE_TYPES.DESKTOP ? (
                    <div className={styles.addToCartBtnContainer}>
                      <Button
                        type={BUTTON_TYPES.OUTLINED}
                        onClick={() => {
                          const currentVariant = selectedVariants[uniqueKey] ?? selectedCardVariant
                          productBuyNowStateManager.showBuyNowPopUp({
                            productCard: productCard,
                            selectedOptions: getSelectedOptionsForProductCard(currentVariant),
                          })
                        }}
                        className={styles.addToCartBtn}
                      >
                        {(selectedVariants[uniqueKey] ?? selectedCardVariant)?.displayConfig.appExclusive
                          ? tCommon('shop_in_the_app').toUpperCase()
                          : t('add_to_cart')}
                      </Button>
                    </div>
                  ) : null}
                </div>
              )
            })}
          </div>
        ) : (
          <EmptyWishList />
        )}
      </div>
    </SmartLoaderWrapper>
  )
}

function EmptyWishList() {
  const router = useRouter()
  const { t } = useTranslation('wishlist')
  return (
    <div className={styles.emptyWishlist}>
      <h2 className={styles.emptyWishlist__title}>{t('your_wishlist_is_empty')}</h2>
      <Button
        onClick={() => {
          router.push('/women')
        }}
        round={true}
        size={BUTTON_SIZE.MEDIUM}
        type={BUTTON_TYPES.SECONDARY}
        className={styles.emptyWishlist__cta}
      >
        {t('shop_now')}
      </Button>
    </div>
  )
}

export default WishlistItemList
